import React from 'react';
import PublicLayout from '../../public/PublicLayout';
import { Link } from 'react-router-dom';
import VendorsDisplay from '../../VendorsDisplay';

// Import collaborator and partner images
import globalAsthmaImg from '../../../assets/collaborators and partners/global Asthma and COPD Outreach.png';
import globalHealthImg from '../../../assets/collaborators and partners/global health alliance.webp';
import iarcImg from '../../../assets/collaborators and partners/IARC-Logo.jpg';
import breatheWellImg from '../../../assets/collaborators and partners/the breathe well initiative.png';

const PartnersAndCollaborators = () => {
  const [collaborators, setCollaborators] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Hardcoded collaborators as fallback (same as HomePage)
  const hardcodedCollaborators = [
    {
      name: 'International Council for Respiratory Care (ICRC)',
      logo: iarcImg,
      url: 'https://irccouncil.org/',
      description: 'Global organization advancing respiratory care education and practice'
    },
    {
      name: 'Global Health Alliance',
      logo: globalHealthImg,
      url: 'https://www.gha21.org',
      description: 'International health organization focused on global health initiatives'
    },
    {
      name: 'Global Asthma and COPD Outreach',
      logo: globalAsthmaImg,
      url: 'http://www.gacoglobal.org',
      description: 'Organization dedicated to asthma and COPD awareness and treatment'
    },
    {
      name: 'The Breath Well Initiative',
      logo: breatheWellImg,
      url: 'http://www.tbwi.org',
      description: 'Initiative focused on improving respiratory health worldwide'
    },
    {
      name: 'World Health Organization (WHO)',
      logo: null,
      url: 'https://www.who.int',
      description: 'United Nations agency for international public health'
    },
    {
      name: 'Canadian Society for Respiratory Therapists (CSRT)',
      logo: null,
      url: 'https://www.csrt.com',
      description: 'Professional organization for respiratory therapists in Canada'
    },
    {
      name: 'European Society for Respiratory Care',
      logo: null,
      url: 'https://www.esrc.eu',
      description: 'European organization promoting respiratory care excellence'
    },
    {
      name: 'American Association for Respiratory Care',
      logo: null,
      url: 'https://www.aarc.org',
      description: 'Professional association for respiratory care in the United States'
    },
    {
      name: 'Africa Thoracic Society',
      logo: null,
      url: 'https://www.africathoracic.org',
      description: 'Pan-African organization for thoracic health professionals'
    }
  ];

  // Fetch collaborators from API (same as HomePage)
  React.useEffect(() => {
    const fetchCollaborators = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const response = await fetch(`${API_BASE}/collaborators/public`);
        const data = await response.json();

        if (data.success && data.data) {
          // Map database fields to component format
          const mappedCollaborators = data.data.map(collab => {
            let logoUrl = null;
            if (collab.logo_image) {
              if (collab.logo_image.startsWith('http')) {
                logoUrl = collab.logo_image;
              } else if (collab.logo_image.startsWith('/uploads/')) {
                // Use BACKEND_URL (without /api) for uploads
                logoUrl = `${BACKEND_URL}${collab.logo_image}`;
              } else {
                logoUrl = collab.logo_image;
              }
            }
            return {
              name: collab.organization_name,
              logo: logoUrl,
              url: collab.website_url || '#',
              description: collab.description || 'Partner organization'
            };
          });
          setCollaborators(mappedCollaborators);
        } else {
          // Use hardcoded fallback if API returns no data
          setCollaborators(hardcodedCollaborators);
        }
      } catch (error) {
        console.error('Error fetching collaborators:', error);
        // Fallback to hardcoded collaborators if API fails
        setCollaborators(hardcodedCollaborators);
      } finally {
        setLoading(false);
      }
    };

    fetchCollaborators();
  }, []);

  return (
    <PublicLayout>
      <div className="py-5 min-vh-100" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #6B8E23 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row justify-content-center">
            <div className="col-12">
              {/* Header */}
              <div className="text-center mb-5">
                <h1 className="display-4 fw-bold mb-3" style={{ color: '#1f2937' }}>Our Partners & Collaborators</h1>
                <p className="lead" style={{ color: '#374151', maxWidth: '800px', margin: '0 auto' }}>
                  ARCA works with leading organizations worldwide to advance respiratory care across Africa.
                  These partnerships help us expand our reach, share knowledge, and improve patient outcomes.
                </p>
              </div>

              {/* Vendor Partners Section - Same as HomePage */}
              <div className="mb-5">
                <VendorsDisplay />
              </div>

              {/* Collaborators Grid */}
              <div className="mb-5">
                <h2 className="h2 fw-bold mb-4 text-center" style={{ color: '#1f2937' }}>Our Collaborators</h2>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
                      <span className="visually-hidden">Loading collaborators...</span>
                    </div>
                    <p className="mt-3" style={{ color: '#4b5563' }}>Loading our partners...</p>
                  </div>
                ) : (
                  <div className="row g-3">
                    {collaborators.map((collaborator, index) => (
                      <div key={index} className="col-6 col-md-4 col-lg-2">
                        <div
                          style={{
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-5px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {/* ARCA Green Header */}
                          <div className="p-3 text-center text-white" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)' }}>
                            <h6 className="fw-bold mb-0" style={{ fontSize: '0.9rem', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {collaborator.name}
                            </h6>
                          </div>

                          {/* Logo Section */}
                          <div className="p-3 bg-light text-center" style={{ minHeight: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {collaborator.logo ? (
                              <img
                                src={collaborator.logo}
                                alt={collaborator.name}
                                className="img-fluid"
                                style={{ maxHeight: '120px', width: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  console.error(`Failed to load image for ${collaborator.name}:`, e.target.src);
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = `<div class="d-flex align-items-center justify-content-center" style="width: 80px; height: 80px; backgroundColor: #6B8E23; borderRadius: 12px"><i class="fas fa-globe" style="fontSize: 2rem; color: white"></i></div>`;
                                }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{
                                  width: '80px',
                                  height: '80px',
                                  backgroundColor: '#6B8E23',
                                  borderRadius: '12px'
                                }}
                              >
                                <i className="fas fa-globe" style={{ fontSize: '2rem', color: 'white' }}></i>
                              </div>
                            )}
                          </div>

                          {/* Visit Website Button */}
                          <div className="bg-transparent border-top p-2">
                            <a
                              href={collaborator.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm w-100 fw-bold text-white"
                              style={{
                                backgroundColor: '#6B8E23',
                                border: 'none',
                                fontSize: '0.85rem'
                              }}
                            >
                              Visit Website
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Partnership Opportunities */}
              <div className="rounded shadow p-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #6B8E23 100%)' }}>
                <h2 className="h3 fw-bold mb-3" style={{ color: '#1f2937' }}>Partnership Opportunities</h2>
                <p className="mb-4" style={{ color: '#4b5563' }}>
                  We're always looking to expand our network of partners who share our vision of improving
                  respiratory care across Africa. If your organization is interested in collaborating with ARCA,
                  we'd love to hear from you.
                </p>
                <div className="d-flex flex-wrap gap-3">
                  <Link
                    to="/get-involved/partner"
                    className="btn fw-bold shadow"
                    style={{ backgroundColor: '#6B8E23', color: 'white', border: 'none' }}
                  >
                    Become a Partner
                  </Link>
                  <Link
                    to="/contact"
                    className="btn fw-bold shadow"
                    style={{ backgroundColor: 'white', color: '#6B8E23', border: '2px solid #6B8E23' }}
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PartnersAndCollaborators;